import React from 'react';
import { DESTINATIONS_LIST } from 'modules/dataSync/constants/prequel';
import { usePrequelContext, useDestinationTable } from 'modules/dataSync/contexts';
import { MemoSearchableText } from 'ui-beta/searchableText';
import { Tooltip } from 'ui/tooltip';
import { BaseCellWrapper } from './BaseCell';

// Default icon component for unknown vendors
const DefaultIcon = () => (
  <div
    style={{
      width: '24px',
      height: '24px',
      borderRadius: '4px',
      backgroundColor: '#f0f0f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      color: '#666',
    }}
    aria-label="Unknown destination type"
  >
    ?
  </div>
);

interface DestinationNameCellProps {
  id: string;
  name: string;
  vendor: string;
}

export const DestinationNameCell: React.FC<DestinationNameCellProps> = React.memo(
  ({ id, name, vendor }) => {
    const { navigateToTransferHistory } = usePrequelContext();
    const { searchQuery } = useDestinationTable();

    const destinationConfig =
      vendor in DESTINATIONS_LIST
        ? DESTINATIONS_LIST[vendor as keyof typeof DESTINATIONS_LIST]
        : undefined;
    const IconComponent = destinationConfig?.Icon || DefaultIcon;
    const destinationName = destinationConfig?.name || 'Unknown Destination';

    const handleClick = () => {
      navigateToTransferHistory(id);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        navigateToTransferHistory(id);
      }
    };

    return (
      <BaseCellWrapper dataTestId="destination-name-cell">
        <Tooltip tooltipContent={destinationName}>
          <IconComponent height={24} width={24} />
        </Tooltip>
        <span
          data-test="destination-name-link"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="button"
          aria-label={`View transfer history for ${name}`}
        >
          <MemoSearchableText isCaseSensitive={false} text={name} searchText={searchQuery || ''} />
        </span>
      </BaseCellWrapper>
    );
  },
);
